import { parseNarrative } from "@hamlak/shared";
import { useMemo, type HTMLAttributes, type ReactNode } from "react";
import styles from "./CharacterMessage.module.css";
import { cx } from "../cx";
import { IconArrowClockwiseCircularLine, IconBrick, IconPictureLine } from "../icons/Icon";
import { NarrativeBlocks } from "./NarrativeBlocks";

export type CharacterMessageProps = HTMLAttributes<HTMLDivElement> & {
  /** who 행 이름(Figma 294:9302). 없으면 who 행 숨김. */
  name?: ReactNode;
  /** who 행 아바타(28 원형으로 잘린다). */
  avatar?: ReactNode;
  /** 지문 (fg-neutral-subtle) */
  narration?: ReactNode;
  /** 대사 (fg-neutral, neutral-weak 말풍선) */
  dialogue?: ReactNode;
  /** 마크다운 본문(shared parseNarrative 규칙). narration·dialogue보다 우선한다. */
  markdown?: string;
  /** 답변 다시 생성 ↻ (CHT-08, 마지막 답변에만). 없으면 버튼 없음. */
  onRegenerate?: () => void;
  /** 장면 생성 (2차). 넘기지 않으면 버튼 없음. */
  imageGenerate?: { cost: number; onClick: () => void };
  /** 스트리밍 중 커서 표시 */
  streaming?: boolean;
  /** 내 메시지로 그린다(AC0-254): 같은 서식 규약, 오른쪽 정렬, 대사는 내 말풍선(MyBubble). who 행·액션 없이 쓴다 */
  mine?: boolean;
  children?: ReactNode;
};

/** 캐릭터 응답 블록 (Figma 88:3185). 카드 없이 who 행 + 본문(장면·지문·대사·속마음·마크다운 블록) + 액션. mine 이면 같은 규약으로 내 메시지. */
export function CharacterMessage({ name, avatar, narration, dialogue, markdown, onRegenerate, imageGenerate, streaming, mine, className, children, ...rest }: CharacterMessageProps) {
  const showActions = onRegenerate || imageGenerate;
  // 스트리밍인데 아직 본문이 없으면(첫 글자 전) pill 말풍선 안 점 3개 펄스(AC0-142, Figma 79:2077). who 행은 그대로 둔다
  const blocks = useMemo(() => (markdown !== undefined ? parseNarrative(markdown, { streaming: !!streaming }) : null), [markdown, streaming]);
  const waiting = streaming && (blocks ? blocks.length === 0 : !narration && !dialogue);
  return (
    <div className={cx(styles.root, mine && styles.rootMine, className)} {...rest}>
      {name && (
        <div className={styles.who}>
          {avatar && <span className={styles.avatar}>{avatar}</span>}
          <span className={styles.name}>{name}</span>
        </div>
      )}
      {waiting && (
        <span className={styles.typing} role="status" aria-label="입력 중" data-testid="typing-dots">
          <i />
          <i />
          <i />
        </span>
      )}
      {blocks && !waiting && <NarrativeBlocks blocks={blocks} streaming={streaming} mine={mine} />}
      {!blocks && narration && (
        <p className={styles.narration} data-kind="narration">
          {narration}
        </p>
      )}
      {!blocks && dialogue && (
        <p className={styles.dialogue} data-kind="dialogue">
          {dialogue}
          {streaming && <span className={styles.cursor} aria-hidden />}
        </p>
      )}
      {children}
      {showActions && !streaming && (
        <div className={styles.actions}>
          {onRegenerate && (
            <button type="button" className={styles.regen} aria-label="다시 생성" onClick={onRegenerate}>
              <IconArrowClockwiseCircularLine size={18} />
            </button>
          )}
          {imageGenerate && (
            <button type="button" className={styles.imageGenerate} onClick={imageGenerate.onClick}>
              <IconPictureLine size={14} />
              <span>장면 생성</span>
              <IconBrick size={14} />
              <b>{imageGenerate.cost}</b>
            </button>
          )}
        </div>
      )}
    </div>
  );
}
